import React from "react";
import moment from "moment";
import { Icon } from "@iconify/react";

const FIELD_LABELS = {
  CACCertificateDoc: "CAC certificate",
  CACCO2Doc: "CAC status report (CO2)",
  CACC07Doc: "CAC particulars of directors (CO7)",
  identityVerificationDoc: "Director's ID",
  utilityBill: "Utility bill",
  tradingName: "Trading name",
  agencyName: "Agency name",
  businessEmail: "Business email",
  businessPhone: "Business phone",
  registeredBusinessName: "Registered business name",
  companyNumber: "RC number",
  taxIdentification: "TIN",
};

const PENDING = new Set([
  "pending",
  "requested",
  "open",
  "awaiting",
  "in_review",
  "in-review",
  "submitted",
]);

function fieldLabel(field) {
  return (
    FIELD_LABELS[field] ||
    String(field || "Field")
      .replace(/([A-Z])/g, " $1")
      .trim()
  );
}

/**
 * Amber banner when:
 * - there is a Pending business-change request, OR
 * - first-time verification (requestedVerification, not verified, no change request)
 */
export default function CustomerVerificationRequestBanner({
  changeRequest,
  initialVerification,
  onReview,
}) {
  const id = changeRequest?._id || changeRequest?.id;
  const status = String(changeRequest?.status || "").toLowerCase();
  const hasPendingChange = Boolean(id && PENDING.has(status));

  if (!hasPendingChange && !initialVerification) return null;

  const changes = Array.isArray(changeRequest?.changes)
    ? changeRequest.changes
    : [];
  const changeSummary = changes
    .map((c) => fieldLabel(c?.field))
    .filter(Boolean)
    .slice(0, 3)
    .join(", ");
  const more = changes.length > 3 ? ` +${changes.length - 3} more` : "";

  return (
    <div className="mx-3 mt-3 rounded-xl border border-[#F0D3A6] bg-[#FFF8EE] px-3.5 py-3 sm:mx-4 sm:px-4">
      <div className="flex flex-wrap items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FBEED9] text-[#B4690E]">
          <Icon
            icon="mdi:alert-circle-outline"
            width={20}
            height={20}
            aria-hidden="true"
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="m-0 text-sm font-semibold text-[#7A4A08]">
            {hasPendingChange
              ? "Business changes waiting for review"
              : "Verification request in progress"}
          </p>
          <p className="m-0 mt-0.5 text-sm leading-snug text-[#7A4A08]/90">
            {hasPendingChange ? (
              <>
                {changeRequest.section ? `${changeRequest.section} · ` : ""}
                {changes.length
                  ? `${changes.length} change${
                      changes.length === 1 ? "" : "s"
                    }`
                  : "Pending request"}
                {changeSummary ? `: ${changeSummary}${more}` : ""}
                {changeRequest.submittedAt
                  ? ` · submitted ${moment(changeRequest.submittedAt).format(
                      "DD MMM, HH:mm",
                    )}`
                  : ""}
                . Open review to compare before and after, then approve or
                reject.
              </>
            ) : (
              <>
                This agency asked for verification for the first time. Open
                review to check their profile and documents, then activate or
                reject.
              </>
            )}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onReview}
              className="inline-flex h-9 items-center justify-center rounded-lg border border-[#D6DEEA] bg-white px-3 text-sm font-semibold text-[#111827] hover:bg-[#F8FAFC]"
            >
              Review request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
